import { Component, OnInit, inject, signal } from '@angular/core';
import { App, AppInfo } from '@capacitor/app';
import { Device } from '@capacitor/device';
import { Store } from '@ngrx/store';
import { isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import { LocalStorageService } from 'ngx-webstorage';
import { combineLatest, concatMap, map, of, tap } from 'rxjs';
import { Logger } from '@app/utils/logger';
import { environment } from '@environments/environment';
import { Session } from '@providers/session/session.service';
import { HeartbeatActions } from './heartbeat.actions';
import { selectHeartbeatMessage } from './heartbeat.selectors';

/**
 * Heartbeat Component.
 *
 * @exports
 * @class HeartbeatComponent
 */
@Component({
  selector: 'app-heartbeat',
  template: ``
})
export class HeartbeatComponent implements OnInit {
  private readonly session = inject(Session);
  private readonly storage = inject(LocalStorageService);
  private readonly store = inject(Store);

  private readonly reconnectAttempts = signal<number>(0);

  private static readonly MAX_RECONNECT_ATTEMPTS = 5;
  private static readonly RECONNECT_INTERVAL_MS = 5000;
  private static readonly TOKEN_CHANGE_CLOSE_CODE = 3003;
  private static readonly WEBSOCKET_URL = environment.API_HEARTBEAT.replace(
    'http',
    'ws'
  );

  private readonly logger = Logger.getInstance('Heartbeat');

  private socket: WebSocket | undefined;

  /**
   * Get token.
   *
   * @readonly
   * @type {string | null}
   * @memberof HeartbeatComponent
   */
  get token(): string | null {
    return this.session.token;
  }

  /**
   * @description Lifecycle hook that is called after data-bound properties are initialized.
   * @memberof HeartbeatComponent
   */
  ngOnInit(): void {
    if (!environment.API_HEARTBEAT) {
      this.logger.warn(
        'API_HEARTBEAT is not configured. Heartbeat WebSocket will not be initialized.'
      );
      return;
    }

    this.logger.info('Initializing Heartbeat WebSocket connection...');
    this.connect();
    this.subscribes();
  }

  /**
   * @description Set up subscriptions to store and storage changes.
   * @memberof HeartbeatComponent
   */
  subscribes(): void {
    this.store.select(selectHeartbeatMessage).subscribe((message) => {
      this.logger.debug('Heartbeat message from store:', message);
      // Send message via WebSocket
      if (message) {
        this.send(message);
      }
    });

    // Reconnect on token change
    this.storage.observe(this.session.keys.token).subscribe(() => {
      this.logger.info('Token changed, reconnecting to Heartbeat WebSocket...');

      if (this.socket) {
        // Close existing socket before reconnecting
        this.disconnect(
          HeartbeatComponent.TOKEN_CHANGE_CLOSE_CODE,
          'Reconnecting due to token change'
        );
      } else {
        // No existing socket, just connect
        this.connect();
      }
    });
  }

  /**
   * @description Connect to WebSocket and set up event handlers.
   * @memberof HeartbeatComponent
   */
  connect(): void {
    const logger = this.logger.child(this.connect.name);

    if (this.socket) {
      if (this.socket.readyState === WebSocket.CONNECTING) {
        logger.info(`Heartbeat WebSocket is currently connecting, waiting...`);
        return;
      }

      if (this.socket.readyState === WebSocket.OPEN) {
        logger.info(
          'Closing existing open Heartbeat WebSocket connection before reconnecting...'
        );
      } else {
        logger.info(
          'Closing existing Heartbeat WebSocket connection before reconnecting...'
        );
      }
    }

    of(true)
      .pipe(
        tap(() => {
          logger.info('Connecting to Heartbeat WebSocket...');
        }),
        concatMap(() =>
          combineLatest([
            App.getInfo().catch(
              () =>
                ({
                  // Fallback to environment version if App info is unavailable
                  version: environment.SENTRY.VERSION.RELEASE
                }) as AppInfo
            ),
            Device.getId()
          ])
        ),
        map(([appInfo, deviceInfo]) => {
          const params = new URLSearchParams();
          // Append auth token if available
          if (this.token) {
            params.append('auth', this.token);
          }
          // Append device ID
          params.append('deviceId', deviceInfo.identifier);
          // Append timezone
          params.append('timezone', DateTime.now().zoneName);
          // Append app version if available
          params.append('appVersion', appInfo.version);
          // Return serialized params
          return params.toString();
        })
      )
      .subscribe({
        /**
         * @description On next.
         * @param {string} params Params.
         */
        next: (params) => {
          const url = `${HeartbeatComponent.WEBSOCKET_URL}?${params}`;
          // Create WebSocket connection
          this.socket = new WebSocket(url);
          this.socket.onopen = this.onopen.bind(this);
          this.socket.onmessage = this.onmessage.bind(this);
          this.socket.onerror = this.onerror.bind(this);
          this.socket.onclose = this.onclose.bind(this);
        },
        /**
         * @description On error.
         * @param {unknown} error Error.
         */
        error: (error: unknown) => {
          logger.error('Unable to connect to Heartbeat WebSocket:', error);
        }
      });
  }

  /**
   * @description Disconnect from WebSocket.
   * @param {number} code Close code.
   * @param {string} reason Close reason.
   * @memberof HeartbeatComponent
   */
  disconnect(code: number, reason: string): void {
    const logger = this.logger.child(this.disconnect.name);

    if (this.socket) {
      logger.info('Disconnecting from Heartbeat WebSocket...');
      this.socket.close(code, reason);
      this.socket = undefined;
    } else {
      logger.info('No active Heartbeat WebSocket connection to disconnect.');
    }
  }

  /**
   * @description WebSocket close event handler.
   * @param {CloseEvent} event Close event.
   * @memberof HeartbeatComponent
   */
  onclose(event: CloseEvent): void {
    const logger = this.logger.child(this.onclose.name);
    const reconnectAttempts = this.reconnectAttempts();
    const maxReconnectAttempts = HeartbeatComponent.MAX_RECONNECT_ATTEMPTS;

    let reconnectInterval = HeartbeatComponent.RECONNECT_INTERVAL_MS;
    // Reset reconnect attempts on token change
    if (event.code === HeartbeatComponent.TOKEN_CHANGE_CLOSE_CODE) {
      this.reconnectAttempts.set(0);
      reconnectInterval = 0;
    }

    logger.info('Heartbeat WebSocket connection closed:', event.reason);

    // Attempt to reconnect
    if (reconnectAttempts < maxReconnectAttempts) {
      logger.info(
        `Reconnecting to Heartbeat WebSocket in ${reconnectInterval / 1000}s (Attempt ${
          reconnectAttempts + 1
        } of ${maxReconnectAttempts})`
      );

      setTimeout(() => {
        this.reconnectAttempts.update((n) => n + 1);
        this.connect();
      }, reconnectInterval);
    } else {
      logger.error(
        'Maximum reconnect attempts reached. Giving up on Heartbeat WebSocket connection.'
      );
    }
  }

  /**
   * @description WebSocket error event handler.
   * @param {Event} event Error event.
   * @memberof HeartbeatComponent
   */
  onerror(event: Event): void {
    const logger = this.logger.child(this.onerror.name);
    logger.error('Heartbeat WebSocket error occurred:', event);
  }

  /**
   * @description WebSocket message event handler.
   * @param {MessageEvent} event Message event.
   * @memberof HeartbeatComponent
   */
  onmessage(event: MessageEvent): void {
    const logger = this.logger.child(this.onmessage.name);
    logger.info(event.data);

    if (typeof event.data === 'string') {
      const payload = this.toJSON(event.data);
      logger.debug('Parsed WebSocket message payload:', payload);
      if (!isEmpty(payload)) {
        if (payload.__typename === 'PurchaseOrderPayment') {
          this.store.dispatch(
            HeartbeatActions.setPurchaseOrderPayment(payload)
          );
        }
      }
    }
  }

  /**
   * @description WebSocket open event handler.
   * @memberof HeartbeatComponent
   */
  onopen(): void {
    const logger = this.logger.child(this.onopen.name);
    logger.info('Heartbeat WebSocket connection opened.');
    // Reset reconnect attempts on successful connection
    this.reconnectAttempts.set(0);
  }

  /**
   * @description Send message via WebSocket.
   * @param {string} message Message to send.
   * @memberof HeartbeatComponent
   */
  send(message: string): void {
    if (this.socket) {
      if (this.socket.readyState === WebSocket.OPEN) {
        this.logger.info('Sending heartbeat message via WebSocket:', message);
        this.socket.send(message);
      } else if (this.socket.readyState === WebSocket.CONNECTING) {
        this.logger.info(
          'Cannot send heartbeat message, WebSocket is still connecting.'
        );
      } else if (this.socket.readyState === WebSocket.CLOSING) {
        this.logger.warn(
          'Cannot send heartbeat message, WebSocket is closing.'
        );
      } else if (this.socket.readyState === WebSocket.CLOSED) {
        this.logger.warn('Cannot send heartbeat message, WebSocket is closed.');
      } else {
        this.logger.warn(
          'Cannot send heartbeat message, WebSocket is not open.'
        );
      }
    } else {
      this.logger.warn(
        'Cannot send heartbeat message, no WebSocket connection established.'
      );
    }
  }

  /**
   * @description Attempt to parse message as JSON, return null if parsing fails.
   * @param {string} message Message to parse.
   * @returns {unknown} Parsed message or null if parsing fails.
   * @memberof HeartbeatComponent
   */
  toJSON(message: string): any {
    try {
      return JSON.parse(message);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      return null;
    }
  }
}
